// Bytegewichteter Upload-Fortschritt (2026-10-02) — reine Funktionen, die
// Pipeline füttert den Zustand, das Modal rendert `overall`.
//
// Das Modal hatte seit 2026-05 bewusst keinen Balken: Der Bundle-POST schiebt
// seine Bytes in Sekunden zum Dienst, der danach ohne Signal Datei für Datei
// an die Cloud PUTtet — ein Balken stand bei 100 %, während die Wartezeit
// erst begann. Seit dem Umbau auf Stream-PUT (`/upload-file`) streamt der
// Dienst große Dateien durch, ihre Client-Bytes sind ehrlich; und das Bundle
// ist auf 90 MB gedeckelt. Deshalb:
//
//   - Vorbereitung (Sammeln, WAV-Rendern, Video-Mux) füllt 0 … PREP_SHARE.
//     Nur der Mux liefert Prozent; die übrigen Schritte sind kurz und
//     bekommen feste Anteile (`prepAt`).
//   - Upload füllt PREP_SHARE … 1 nach Bytes über Bundle + große Dateien.
//     Bundle-Bytes zählen beim Senden nur bis BUNDLE_SEND_SHARE seines
//     Anteils; der Rest kommt mit der Serverantwort (Stage „saving“, der
//     Balken steht ehrlich still). Große Dateien zählen eins zu eins.
//   - 1 erst mit `done` — davor höchstens 0,995, weil die letzte
//     Serverantwort noch aussteht.

export const PREP_SHARE = 0.15;
export const BUNDLE_SEND_SHARE = 0.8;
const BEFORE_DONE_MAX = 0.995;

export type PrepStep = 'collecting' | 'rendering-voice' | 'rendering-mixed' | 'muxing-video';

/** Anteil der Vorbereitung je Schritt (Beginn … Ende, in 0 … 1). */
const PREP_STEPS: Record<PrepStep, [number, number]> = {
  collecting: [0, 0.1],
  'rendering-voice': [0.1, 0.35],
  'rendering-mixed': [0.35, 0.55],
  'muxing-video': [0.55, 1],
};

/** Vorbereitungsstand beim Beginn eines Schritts, optional mit Anteil innerhalb (Mux-Prozent). */
export function prepAt(step: PrepStep, fraction = 0): number {
  const [start, end] = PREP_STEPS[step];
  return start + Math.min(1, Math.max(0, fraction)) * (end - start);
}

export interface ProgressState {
  /** Vorbereitung 0 … 1 (1 = alle Dateien gebaut). */
  prep: number;
  /** Bytes des Multipart-Bundles (0 = noch unbekannt). */
  bundleBytes: number;
  bundleSent: number;
  /** Serverantwort auf das Bundle liegt vor (Version reserviert, Dateien in der Cloud). */
  bundleSaved: boolean;
  /** Summe der großen Dateien (`/upload-file`). */
  largeTotal: number;
  /** Gesendete Bytes über alle großen Dateien (fertige + laufende). */
  largeSent: number;
  /** Alle Bytes der laufenden Anfrage sind raus, die Antwort steht aus. */
  awaitingServer: boolean;
  done: boolean;
}

export function createProgress(): ProgressState {
  return { prep: 0, bundleBytes: 0, bundleSent: 0, bundleSaved: false, largeTotal: 0, largeSent: 0, awaitingServer: false, done: false };
}

export type ProgressStage = 'preparing' | 'uploading' | 'saving';

export function progressStage(s: ProgressState): ProgressStage {
  if (s.prep < 1 || s.bundleBytes + s.largeTotal <= 0) return 'preparing';
  return s.awaitingServer ? 'saving' : 'uploading';
}

/** Gesendete / gesamte Upload-Bytes (für „123 / 180 MB“), jeweils gekappt. */
export function progressBytes(s: ProgressState): { sent: number; total: number } {
  const total = s.bundleBytes + s.largeTotal;
  const sent = Math.min(s.bundleSent, s.bundleBytes) + Math.min(s.largeSent, s.largeTotal);
  return { sent: Math.min(sent, total), total };
}

export function progressRatio(s: ProgressState): number {
  if (s.done) return 1;
  const prep = PREP_SHARE * Math.min(1, Math.max(0, s.prep));
  const total = s.bundleBytes + s.largeTotal;
  if (s.prep < 1 || total <= 0) return prep;
  const bundleSendPart = s.bundleBytes > 0 ? Math.min(s.bundleSent / s.bundleBytes, 1) * BUNDLE_SEND_SHARE : 0;
  const bundleSavedPart = s.bundleSaved ? 1 - BUNDLE_SEND_SHARE : 0;
  const bundle = s.bundleBytes * (bundleSendPart + bundleSavedPart);
  const large = Math.min(s.largeSent, s.largeTotal);
  const ratio = PREP_SHARE + (1 - PREP_SHARE) * ((bundle + large) / total);
  return Math.min(BEFORE_DONE_MAX, Math.max(PREP_SHARE, ratio));
}
